import { Box, Divider } from '@material-ui/core'
import React, { useState, useEffect } from 'react'
import {
  DateField,
  EditButton,
  FunctionField,
  GET_ONE,
  ReferenceField,
  sanitizeFetchType,
  Show,
  ShowProps,
  Tab,
  TabbedShowLayout,
  TextField,
  TitleProps,
  useShowContext,
  useTranslate,
  useQuery,
  useQueryWithStore,
} from 'react-admin'
import { Authority } from '../../../../core/auth/Authority'
import { ResourceName } from '../../../../core/ResourceName'
import { useHasAuthority } from '../../../hooks/useHasAuthority'
import { CameraDto } from '../../../../core/dto/device/camera/cameras/camera.dto'
import { OpenCameraPreviewDialogButton } from './CameraPreviewDialog'
import { CameraServerDto } from '../../../../core/dto/device/camera/camera-servers/camera-server.dto'
import { DeviceStatusesTab } from '../../common/modules/ModuleStatus'
import { DeviceTypes } from '../../../../core/enum/DeviceTypes'
import { BlankActions } from '../../../common/BlankActions'

interface CameraTitleProps extends TitleProps {
  record?: CameraDto
}

const ShowTitle = (data: CameraTitleProps) => {
  const { record: camera } = data
  const translate = useTranslate()
  return (
    <span>
      {translate('resources.cameras.titles.show')}: {camera?.id}.{' '}
      {camera?.description}
    </span>
  )
}

const GeneralActions = () => {
  const { record: camera } = useShowContext<CameraDto>()
  const hasAuthority = useHasAuthority()
  return (
    <>
      <Divider />
      <Box p={1} display="block" textAlign="right">
        <OpenCameraPreviewDialogButton
          props={{ color: 'primary' }}
          cameraData={camera?.id ? { id: camera?.id } : undefined}
        />
        <EditButton
          basePath={`/${ResourceName.CAMERAS}`}
          record={camera}
          variant="contained"
          disabled={!hasAuthority(Authority.EDIT_CAMERAS)}
        />
      </Box>
    </>
  )
}

export const CameraTabs = ({ ...props }) => {
  const hasAuthority = useHasAuthority()
  const { simpleView, createdAt, ...rest } = props
  const { record: camera } = rest
  const [cameraServerType, SetCameraServerType] = useState<string | undefined>(
    undefined,
  )

  const { data: serverDate } = useQueryWithStore({
    type: sanitizeFetchType('getServerDateTime'),
    resource: ResourceName.CURRENT_USER,
    payload: {},
  })
  const { data: cameraServer } = useQuery(
    {
      type: sanitizeFetchType(GET_ONE),
      resource: ResourceName.CAMERA_SERVERS,
      payload: { id: camera.serverId },
    },
    {
      enabled: hasAuthority(Authority.VIEW_CAMERA_SERVERS) && !cameraServerType,
    },
  )

  useEffect(() => {
    if (cameraServer) SetCameraServerType(cameraServer?.type)
  }, [cameraServer])

  return (
    <TabbedShowLayout {...props} syncWithLocation={!simpleView}>
      <Tab label="resources.cameras.tabs.general">
        <TextField source="id" label="ID" />
        <TextField source="description" />
        <TextField source="serverSideName" />
        <DateField
          source="lastRefresh"
          showTime
          options={{
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          }}
        />
        {hasAuthority(Authority.VIEW_CAMERA_SERVERS) && (
          <ReferenceField
            source="serverId"
            link="show"
            reference={ResourceName.CAMERA_SERVERS}
          >
            <FunctionField<CameraServerDto>
              source="name"
              render={(server?: CameraServerDto) =>
                `${server?.id}. ${server?.name}`
              }
            />
          </ReferenceField>
        )}
        {(!simpleView && <GeneralActions />) || <BlankActions />}
      </Tab>
      <Tab label="resources.cameras.tabs.statuses" path="statuses">
        {camera?.jsonStatus && (
          <DeviceStatusesTab
            jsonStatus={camera?.jsonStatus}
            deviceType={DeviceTypes.CAMERA}
            cameraType={cameraServerType}
            referenceDateTime={createdAt || serverDate?.serverDateTime}
          />
        )}
      </Tab>
    </TabbedShowLayout>
  )
}

export const CameraShow = (props: ShowProps) => (
  <Show {...props} actions={false} title={<ShowTitle />}>
    <CameraTabs />
  </Show>
)
